import * as React from "react";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import NotesIcon from "@mui/icons-material/NotesRounded";
import AddIcon from "@mui/icons-material/AddRounded";
import UndoIcon from "@mui/icons-material/UndoRounded";
import type { LlmCostEstimates, LlmRole, LlmSettings } from "../../models/domain";
import ModelPicker from "./ModelPicker";
import { differentCost, formatUsd, unitCost, type UnitKind } from "./costs";
import { REASONING_EFFORTS, acceptsTemperature, capsOf } from "./llmCatalog";
import { SUBJECT_ROLES, effectiveValue, ownValue, removeSubject, setValue, type LlmField, type RoleMeta } from "./llmSettings";

export interface SubjectRow {
  id: string;
  name: string;
  program: string;
}

interface Props {
  llm: LlmSettings;
  onChange: (llm: LlmSettings) => void;
  subjects: SubjectRow[];
  /** Le modèle tapé n'est pas déclaré : la page ouvre « Déclarer un modèle ». */
  onDeclare: (provider: string, model: string) => void;
  estimates: LlmCostEstimates | null;
  disabled?: boolean;
}

// Une cellule vide affiche la valeur HÉRITÉE du global, en grisé : c'est ce que
// le service utilisera pour cette matière tant que rien n'y est posé.
const inheritedSx = { "& input::placeholder": { color: "text.disabled", opacity: 1, fontStyle: "italic" } };

const TemperatureCell: React.FC<{
  value: number | undefined;
  placeholder: string;
  onChange: (v: number | undefined) => void;
  disabled?: boolean;
  label: string;
}> = ({ value, placeholder, onChange, disabled, label }) => (
  <TextField
    size="small"
    type="number"
    disabled={disabled}
    value={value ?? ""}
    placeholder={placeholder}
    onChange={(e) => onChange(e.target.value === "" ? undefined : Number(e.target.value))}
    inputProps={{ min: 0, max: 1, step: 0.05, "aria-label": label }}
    sx={{ width: 96, ...inheritedSx }}
  />
);

/** Effort de raisonnement : posé ici, ou celui du global (grisé). */
const EffortCell: React.FC<{
  value: string | undefined;
  placeholder: string;
  onChange: (v: string | undefined) => void;
  disabled?: boolean;
  label: string;
}> = ({ value, placeholder, onChange, disabled, label }) => (
  <TextField
    select
    size="small"
    disabled={disabled}
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value || undefined)}
    SelectProps={{ displayEmpty: true }}
    inputProps={{ "aria-label": label }}
    sx={{ width: 140 }}
  >
    <MenuItem value="">
      <Typography variant="body2" color="text.disabled" sx={{ fontStyle: "italic" }}>
        {placeholder}
      </Typography>
    </MenuItem>
    {REASONING_EFFORTS.map((e) => (
      <MenuItem key={e} value={e}>
        {e}
      </MenuItem>
    ))}
  </TextField>
);

/** Bouton de la consigne : posée ici, ou héritée du global (grisée). */
const InstructionsCell: React.FC<{
  own: string | undefined;
  inherited: string | undefined;
  onEdit: () => void;
  disabled?: boolean;
}> = ({ own, inherited, onEdit, disabled }) => (
  <Tooltip title={own || inherited || "Aucune consigne"}>
    <span>
      <Button
        size="small"
        variant={own ? "outlined" : "text"}
        startIcon={<NotesIcon />}
        disabled={disabled}
        onClick={onEdit}
        sx={{ color: own ? undefined : "text.disabled", fontStyle: own ? undefined : "italic", whiteSpace: "nowrap" }}
      >
        {own ? "Consigne" : inherited ? "Consigne globale" : "Consigne…"}
      </Button>
    </span>
  </Tooltip>
);

const UNIT_SHORT: Record<UnitKind, string> = { objectives: "/ leçon", facts: "/ objectif", quests: "/ quête" };

/** Le coût de la matière, seulement là où il diffère du global. */
const CostCell: React.FC<{ llm: LlmSettings; subjectId: string; estimates: LlmCostEstimates | null }> = ({
  llm,
  subjectId,
  estimates,
}) => {
  const lines = (["objectives", "facts", "quests"] as UnitKind[])
    .map((kind) => ({ kind, own: unitCost(estimates, llm, subjectId, kind), global: unitCost(estimates, llm, "", kind) }))
    .filter((l) => differentCost(l.own, l.global));
  if (lines.length === 0) {
    return (
      <Typography variant="caption" color="text.disabled" sx={{ fontStyle: "italic" }}>
        comme le global
      </Typography>
    );
  }
  return (
    <Stack spacing={0.5}>
      {lines.map((l) => (
        <Typography key={l.kind} variant="body2" sx={{ whiteSpace: "nowrap" }}>
          {l.own ? `≈ ${formatUsd(l.own.usd)}` : "non chiffrable"} {UNIT_SHORT[l.kind]}
          <Typography component="span" variant="caption" color="text.secondary">
            {" "}
            (global {l.global ? formatUsd(l.global.usd) : "—"})
          </Typography>
        </Typography>
      ))}
    </Stack>
  );
};

interface Editing {
  subjectId: string;
  subjectName: string;
  role: RoleMeta;
  text: string;
}

/**
 * « Réglages par matière » : SEULEMENT les matières qui s'écartent du global.
 * Une cellule vide hérite du global (affiché en grisé). « Ajouter une
 * matière » ouvre une ligne vide ; « Retirer » efface ses écarts et la matière
 * revient au global.
 */
export const SubjectSettingsTable: React.FC<Props> = ({ llm, onChange, subjects, onDeclare, estimates, disabled }) => {
  // Une matière ajoutée n'a encore aucun écart, donc aucune entrée dans le
  // JSON : sa ligne tient dans cet état jusqu'à son premier réglage.
  const [added, setAdded] = React.useState<string[]>([]);
  const [picking, setPicking] = React.useState<{ id: string; label: string } | null>(null);
  const [editing, setEditing] = React.useState<Editing | null>(null);

  const byId = React.useMemo(() => new Map(subjects.map((s) => [s.id, s])), [subjects]);
  const rowIds = React.useMemo(() => {
    const ids = [...Object.keys(llm.subjects ?? {}), ...added.filter((id) => !llm.subjects?.[id])];
    return ids.sort((a, b) => (byId.get(a)?.name ?? a).localeCompare(byId.get(b)?.name ?? b, "fr"));
  }, [llm.subjects, added, byId]);
  const options = React.useMemo(
    () =>
      subjects
        .filter((s) => !rowIds.includes(s.id))
        .map((s) => ({ id: s.id, label: s.program ? `${s.name} — ${s.program}` : s.name })),
    [subjects, rowIds],
  );

  const set = (subjectId: string, role: LlmRole, field: LlmField, v: string | number | undefined) => {
    // La ligne reste à l'écran même quand on vide son dernier écart pour le
    // retaper : seul « Retirer » la fait partir.
    setAdded((a) => (a.includes(subjectId) ? a : [...a, subjectId]));
    onChange(setValue(llm, subjectId, role, field, v));
  };

  const remove = (subjectId: string) => {
    setAdded((a) => a.filter((id) => id !== subjectId));
    onChange(removeSubject(llm, subjectId));
  };

  const renderCell = (subjectId: string, subjectName: string, meta: RoleMeta) => {
    const own = (f: LlmField) => ownValue(llm, subjectId, meta.role, f);
    const global = (f: LlmField) => effectiveValue(llm, "", meta.role, f).value;
    const eff = (f: LlmField) => effectiveValue(llm, subjectId, meta.role, f).value as string | undefined;
    const caps = capsOf(llm, eff("provider"), eff("model"));
    return (
      <Stack spacing={1}>
        <ModelPicker
          role={meta.role}
          llm={llm}
          provider={(own("provider") as string) ?? ""}
          model={(own("model") as string) ?? ""}
          inherited={{ provider: global("provider") as string | undefined, model: global("model") as string | undefined }}
          onProvider={(v) => set(subjectId, meta.role, "provider", v)}
          onModel={(v) => set(subjectId, meta.role, "model", v)}
          onDeclare={onDeclare}
          label={`${meta.label} — ${subjectName}`}
          size="small"
          disabled={disabled}
        />
        {meta.temperature && acceptsTemperature(caps) && (
          <TemperatureCell
            value={own("temperature") as number | undefined}
            placeholder={`${global("temperature") ?? ""}`}
            onChange={(v) => set(subjectId, meta.role, "temperature", v)}
            disabled={disabled}
            label={`Température — ${meta.label} — ${subjectName}`}
          />
        )}
        {caps?.reasoning && (
          <EffortCell
            value={own("reasoning_effort") as string | undefined}
            placeholder={(global("reasoning_effort") as string | undefined) ?? "effort par défaut"}
            onChange={(v) => set(subjectId, meta.role, "reasoning_effort", v)}
            disabled={disabled}
            label={`Effort de raisonnement — ${meta.label} — ${subjectName}`}
          />
        )}
        {meta.instructions && (
          <InstructionsCell
            own={own("instructions") as string | undefined}
            inherited={global("instructions") as string | undefined}
            disabled={disabled}
            onEdit={() => setEditing({ subjectId, subjectName, role: meta, text: (own("instructions") as string) ?? "" })}
          />
        )}
      </Stack>
    );
  };

  return (
    <Card>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ sm: "center" }}
        justifyContent="space-between"
        sx={{ p: 2.5 }}
      >
        <Box>
          <Typography variant="h5">Réglages par matière</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            Seules les matières qui s'écartent du global sont listées. Une cellule vide prend la valeur du global, affichée
            en grisé.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Autocomplete
            size="small"
            disabled={disabled}
            options={options}
            value={picking}
            onChange={(_, v) => setPicking(v)}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(p) => <TextField {...p} placeholder="Choisir une matière" />}
            sx={{ minWidth: 280 }}
          />
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            disabled={disabled || !picking}
            onClick={() => {
              if (!picking) return;
              setAdded((a) => [...a, picking.id]);
              setPicking(null);
            }}
            sx={{ whiteSpace: "nowrap" }}
          >
            Ajouter une matière
          </Button>
        </Stack>
      </Stack>
      {rowIds.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ px: 2.5, pb: 2.5 }}>
          Aucune matière ne s'écarte du global : toutes utilisent les réglages des cartes ci-dessus.
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 180 }}>Matière</TableCell>
                {SUBJECT_ROLES.map((r) => (
                  <TableCell key={r.role} sx={{ minWidth: 280, verticalAlign: "bottom" }}>
                    <Typography variant="subtitle2">{r.label}</Typography>
                  </TableCell>
                ))}
                <TableCell sx={{ minWidth: 150, verticalAlign: "bottom" }}>
                  <Typography variant="subtitle2">Coût estimé</Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rowIds.map((id) => {
                const s = byId.get(id);
                const name = s?.name ?? id;
                return (
                  <TableRow key={id}>
                    <TableCell sx={{ verticalAlign: "top" }}>
                      <Typography variant="body2" sx={{ fontWeight: 650 }}>
                        {name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" component="div">
                        {s?.program ?? "matière inconnue du catalogue"}
                      </Typography>
                      <Button
                        size="small"
                        color="inherit"
                        startIcon={<UndoIcon />}
                        disabled={disabled}
                        onClick={() => remove(id)}
                        sx={{ mt: 1, color: "text.secondary" }}
                      >
                        Retirer
                      </Button>
                    </TableCell>
                    {SUBJECT_ROLES.map((r) => (
                      <TableCell key={r.role} sx={{ verticalAlign: "top" }}>
                        {renderCell(id, name, r)}
                      </TableCell>
                    ))}
                    <TableCell sx={{ verticalAlign: "top" }}>
                      <CostCell llm={llm} subjectId={id} estimates={estimates} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={Boolean(editing)} onClose={() => setEditing(null)} maxWidth="sm" fullWidth>
        {editing && (
          <>
            <DialogTitle>
              Consigne — {editing.role.label} — {editing.subjectName}
            </DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Ajoutée à la fin du message, après le prompt de base, à chaque appel de ce rôle pour cette matière. Elle
                REMPLACE la consigne globale ; vide = la consigne globale.
              </Typography>
              <TextField
                autoFocus
                multiline
                minRows={5}
                fullWidth
                value={editing.text}
                onChange={(e) => setEditing({ ...editing, text: e.target.value })}
                inputProps={{ maxLength: 2000 }}
                helperText={`${editing.text.length} / 2000`}
                placeholder={(effectiveValue(llm, "", editing.role.role, "instructions").value as string | undefined) ?? ""}
                sx={{ "& textarea::placeholder": { color: "text.disabled", opacity: 1, fontStyle: "italic" } }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setEditing(null)}>Annuler</Button>
              <Box sx={{ flex: 1 }} />
              <Button
                variant="contained"
                onClick={() => {
                  set(editing.subjectId, editing.role.role, "instructions", editing.text);
                  setEditing(null);
                }}
              >
                Valider
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Card>
  );
};

export default SubjectSettingsTable;
